/**
 * Enhanced Error Handling System
 * Provides structured error classification and user-friendly messaging
 */

// Error types for classification
export const ERROR_TYPES = {
  NETWORK: 'network',
  VALIDATION: 'validation',
  SERVER: 'server',
  TIMEOUT: 'timeout',
  UNKNOWN: 'unknown'
};

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

/**
 * Enhanced Error class with additional context and user-friendly messaging
 */
export class EnhancedError extends Error {
  constructor({
    type = ERROR_TYPES.UNKNOWN,
    message,
    details,
    severity = ERROR_SEVERITY.MEDIUM,
    actionable = false,
    suggestedAction = '',
    retryable = false,
    context = {},
    originalError = null
  }) {
    super(message);
    this.name = 'EnhancedError';
    this.type = type;
    this.details = details;
    this.severity = severity;
    this.actionable = actionable;
    this.suggestedAction = suggestedAction;
    this.retryable = retryable;
    this.context = context;
    this.timestamp = new Date();
    this.originalError = originalError;
  }

  /**
   * Convert to plain object for logging/serialization
   */
  toJSON() {
    return {
      name: this.name,
      type: this.type,
      message: this.message,
      details: this.details,
      severity: this.severity,
      actionable: this.actionable,
      suggestedAction: this.suggestedAction,
      retryable: this.retryable,
      context: this.context,
      timestamp: this.timestamp,
      stack: this.stack
    };
  }
}

/**
 * Error message templates for different error types
 */
const ERROR_MESSAGES = {
  [ERROR_TYPES.NETWORK]: {
    message: 'Network connection failed',
    details: 'Unable to connect to the server. Check your internet connection.',
    suggestedAction: 'Check your internet connection and try again.',
    retryable: true,
    severity: ERROR_SEVERITY.HIGH
  },
  [ERROR_TYPES.TIMEOUT]: {
    message: 'Request timed out',
    details: 'The server took too long to respond.',
    suggestedAction: 'The server may be busy. Try again in a moment.',
    retryable: true,
    severity: ERROR_SEVERITY.MEDIUM
  },
  [ERROR_TYPES.SERVER]: {
    message: 'Server error occurred',
    details: 'The server encountered an internal error.',
    suggestedAction: 'Try again later. If the problem persists, contact support.',
    retryable: true,
    severity: ERROR_SEVERITY.HIGH
  },
  [ERROR_TYPES.VALIDATION]: {
    message: 'Invalid filter combination',
    details: 'The selected filters cannot be applied together.',
    suggestedAction: 'Adjust your filter selection and try again.',
    retryable: false,
    severity: ERROR_SEVERITY.LOW
  },
  [ERROR_TYPES.UNKNOWN]: {
    message: 'An unexpected error occurred',
    details: 'Something went wrong, but we\'re not sure what.',
    suggestedAction: 'Try refreshing the page or contact support if the problem continues.',
    retryable: false,
    severity: ERROR_SEVERITY.MEDIUM
  }
};

/**
 * Classify errors based on various conditions
 */
export function classifyError(error) {
  // Axios/Network errors
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return ERROR_TYPES.TIMEOUT;
  }

  if (error.code === 'NETWORK_ERROR') {
    return ERROR_TYPES.NETWORK;
  }

  // Check for actual network errors (no response received)
  if (error.request && !error.response) {
    return ERROR_TYPES.NETWORK;
  }

  // HTTP status based classification
  if (error.response) {
    const status = error.response.status;

    if (status >= 400 && status < 500) {
      // Client errors - likely validation issues
      if (status === 400) return ERROR_TYPES.VALIDATION;
      if (status === 422) return ERROR_TYPES.VALIDATION;
      return ERROR_TYPES.VALIDATION;
    }

    if (status >= 500) {
      return ERROR_TYPES.SERVER;
    }
  }

  // API response error type
  if (error.response?.data?.error?.type) {
    return error.response.data.error.type;
  }

  return ERROR_TYPES.UNKNOWN;
}

/**
 * Create user-friendly error from raw error
 */
export function createEnhancedError(originalError, context = {}) {
  const errorType = classifyError(originalError);
  const template = ERROR_MESSAGES[errorType];

  // Extract API error details if available
  const apiError = originalError.response?.data?.error;

  return new EnhancedError({
    type: errorType,
    message: apiError?.message || template.message,
    details: apiError?.details || template.details,
    severity: apiError?.severity || template.severity,
    actionable: true,
    suggestedAction: apiError?.suggestedAction || template.suggestedAction,
    retryable: apiError?.retryable !== undefined ? apiError.retryable : template.retryable,
    context,
    originalError
  });
}

/**
 * Specific error creators for common scenarios
 */
export const ErrorCreators = {
  /**
   * Create filter validation error
   */
  filterValidation(message, details, context = {}) {
    return new EnhancedError({
      type: ERROR_TYPES.VALIDATION,
      message,
      details,
      severity: ERROR_SEVERITY.LOW,
      actionable: true,
      suggestedAction: 'Adjust your filter selection and try again.',
      retryable: false,
      context
    });
  },

  /**
   * Create network connectivity error
   */
  networkError(context = {}) {
    return new EnhancedError({
      type: ERROR_TYPES.NETWORK,
      message: 'Unable to connect to server',
      details: 'Network connection failed. Please check your internet connection.',
      severity: ERROR_SEVERITY.HIGH,
      actionable: true,
      suggestedAction: 'Check your internet connection and refresh the page.',
      retryable: true,
      context
    });
  },

  /**
   * Create server error
   */
  serverError(status = 500, context = {}) {
    return new EnhancedError({
      type: ERROR_TYPES.SERVER,
      message: 'Server error occurred',
      details: `Server returned error status ${status}`,
      severity: ERROR_SEVERITY.HIGH,
      actionable: true,
      suggestedAction: 'Try again in a few minutes. Contact support if the problem persists.',
      retryable: true,
      context
    });
  },

  /**
   * Create timeout error
   */
  timeoutError(context = {}) {
    return new EnhancedError({
      type: ERROR_TYPES.TIMEOUT,
      message: 'Request timed out',
      details: 'The request took too long to complete.',
      severity: ERROR_SEVERITY.MEDIUM,
      actionable: true,
      suggestedAction: 'The server may be busy. Try again in a moment.',
      retryable: true,
      context
    });
  }
};

/**
 * Error logging utility
 */
export class ErrorLogger {
  constructor() {
    this.errors = [];
    this.maxErrors = 100; // Keep last 100 errors
  }

  /**
   * Log an enhanced error
   */
  log(error, context = {}) {
    const logEntry = {
      timestamp: new Date(),
      error: error instanceof EnhancedError ? error.toJSON() : error,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    this.errors.push(logEntry);

    // Keep only recent errors
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(-this.maxErrors);
    }

    // Console logging based on severity
    if (error.severity === ERROR_SEVERITY.CRITICAL) {
      console.error('CRITICAL ERROR:', logEntry);
    } else if (error.severity === ERROR_SEVERITY.HIGH) {
      console.error('High severity error:', logEntry);
    } else if (error.severity === ERROR_SEVERITY.MEDIUM) {
      console.warn('Medium severity error:', logEntry);
    } else {
      console.log('Low severity error:', logEntry);
    }
  }

  /**
   * Get recent errors
   */
  getRecentErrors(count = 10) {
    return this.errors.slice(-count);
  }

  /**
   * Get errors by type
   */
  getErrorsByType(type) {
    return this.errors.filter(entry =>
      entry.error.type === type
    );
  }

  /**
   * Clear error log
   */
  clear() {
    this.errors = [];
  }
}

// Singleton error logger instance
export const errorLogger = new ErrorLogger();

/**
 * Global error handler setup
 */
export function setupGlobalErrorHandling() {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = createEnhancedError(event.reason, {
      type: 'unhandled_promise_rejection'
    });
    errorLogger.log(error);
  });

  // Handle general JavaScript errors
  window.addEventListener('error', (event) => {
    const error = new EnhancedError({
      type: ERROR_TYPES.UNKNOWN,
      message: event.message,
      details: `Error in ${event.filename} at line ${event.lineno}:${event.colno}`,
      severity: ERROR_SEVERITY.MEDIUM,
      actionable: false,
      context: {
        type: 'javascript_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    });
    errorLogger.log(error);
  });
}